import { cn } from '@quadralize/utils'
import type * as React from 'react'
import type { SchedulerDensity } from './types'
import { getWeekDays, toIso } from './utils'

// ─── Types ────────────────────────────────────────────────────────────────────

interface WeekDayTabsProps {
  date: Date
  activeIso: string
  density?: SchedulerDensity
  onSelect: (iso: string) => void
}

// Single-letter weekday initials (Sun → Sat)
const DAY_INITIALS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const

// ─── Component ────────────────────────────────────────────────────────────────

export const WeekDayTabs = ({
  date,
  activeIso,
  density = 'comfortable',
  onSelect,
}: WeekDayTabsProps): React.JSX.Element => {
  const days = getWeekDays(date)
  const todayIso = toIso(new Date())
  const isCompact = density === 'compact'

  return (
    <div
      className={cn(
        'grid w-full min-w-0 grid-cols-7 border-b border-border-default bg-bg-subtle/90 shrink-0 gap-1 supports-backdrop-filter:backdrop-blur-md',
        isCompact ? 'px-1.5 py-1.5' : 'px-2 py-2',
      )}
    >
      {days.map((d) => {
        const iso = toIso(d)
        const isActive = iso === activeIso
        const isToday = iso === todayIso

        return (
          <button
            key={iso}
            type="button"
            onClick={() => onSelect(iso)}
            aria-pressed={isActive}
            className={cn(
              'flex min-w-0 flex-col items-center gap-0.5 rounded-xl border border-transparent transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
              isCompact ? 'px-1 py-1' : 'px-1.5 py-1.5',
              isActive
                ? 'border-border-default bg-bg-base text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-muted/60',
            )}
          >
            <span
              className={cn(
                'font-medium uppercase tracking-wider',
                isCompact ? 'text-[9px]' : 'text-[10px]',
                isActive ? 'text-primary/80' : 'text-text-tertiary',
              )}
            >
              {DAY_INITIALS[d.getDay()]}
            </span>
            <span
              className={cn(
                'flex items-center justify-center rounded-full font-bold tabular-nums transition-colors',
                isCompact ? 'w-6 h-6 text-xs' : 'w-7 h-7 text-sm',
                isActive && isToday && 'bg-primary text-white',
                isActive && !isToday && 'bg-primary/10 text-primary',
                !isActive && isToday && 'text-primary font-extrabold',
                !isActive && !isToday && 'text-text-primary',
              )}
            >
              {d.getDate()}
            </span>
            <span
              className={cn(
                'h-1 w-1 rounded-full transition-colors',
                isToday ? 'bg-primary/70' : 'bg-transparent',
              )}
            />
          </button>
        )
      })}
    </div>
  )
}

WeekDayTabs.displayName = 'WeekDayTabs'
